"use client";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { fetchDispute, validateTxid } from "@/utils/api/dispute";


export default function DisputeModal({ open, onClose }) {
  const [txid, setTxid] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState(null); // {transactionId,status,step,message}
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const pollRef = useRef(null);
  const inputRef = useRef(null);

 useEffect(() => {
  if (!open) return;

  const body = document.body;
  const docEl = document.documentElement;

  const isTouch = matchMedia("(pointer:coarse)").matches;
  const prevOverflow = body.style.overflow;
  const prevPaddingRight = body.style.paddingRight;
  const prevPosition = body.style.position;
  const prevTop = body.style.top;
  const prevWidth = body.style.width;
  const scrollY = window.scrollY;

  // Desktop: keep your scrollbar compensation
  if (!isTouch) {
    const scrollbarComp = window.innerWidth - docEl.clientWidth;
    body.style.overflow = "hidden";
    if (scrollbarComp > 0) {
      body.style.paddingRight = `${scrollbarComp}px`;
    }
  } else {
    // Mobile: use "position: fixed" lock to prevent jitter
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%"; // avoid sideways wobble
    body.style.overflow = "hidden";
  }

  return () => {
    body.style.overflow = prevOverflow;
    body.style.paddingRight = prevPaddingRight;
    body.style.position = prevPosition;
    body.style.top = prevTop;
    body.style.width = prevWidth;
    if (isTouch) {
      // restore scroll position
      window.scrollTo(0, scrollY);
    }
  };
}, [open]);

  
  function stopPolling() {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }

  function isActive(status) {
    return ["PENDING", "AUTHORIZED", "IN_PROGRESS"].includes(String(status || ""));
  }

  function maybePoll(nextData) {
    if (isActive(nextData?.status)) {
      if (!pollRef.current) {
        pollRef.current = setInterval(() => onSubmit(null, { silent: true }), 1500);
      }
    } else {
      stopPolling();
    }
  }

  async function onSubmit(e, opts = {}) {
    e?.preventDefault?.();
    setError("");
    let clean;
    try {
      clean = validateTxid(txid);
    } catch (err) {
      setError(err.message);
      return;
    }
    if (!opts.silent) setLoading(true);
    try {
      const data = await fetchDispute(clean);
      setStatus(data);
      setLastUpdated(new Date());
      maybePoll(data);
    } catch (err) {
      setError(err.message || "Unable to check");
      setStatus(null);
      stopPolling();
    } finally {
      if (!opts.silent) setLoading(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* backdrop */}
      <button
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-label="Close"
      />
      {/* modal */}
      <div className="relative w-full max-w-[90vw] sm:max-w-lg rounded-2xl bg-surface text-textPrimary border border-border shadow-xl animate-fadeIn">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-lg font-semibold">Check Transaction Status</h2>
          <button
            onClick={onClose}
            className="px-2 py-1 rounded-lg border border-border hover:bg-background"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-5 space-y-3">
          <label className="block text-sm text-textSecondary">Your Transaction ID</label>
       <input
  ref={inputRef}
  className="w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-[16px] sm:text-sm focus:outline-none focus:ring-1 focus:ring-accent overflow-hidden text-ellipsis"
  placeholder="e.g. pi_68e9...c19f"
  value={txid}
  onChange={(e) => setTxid(e.target.value)}
  autoComplete="off"
  spellCheck="false"
  inputMode="text"
/>


          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl border border-border px-4 py-2 hover:bg-background disabled:opacity-60"
          >
            {loading ? "Checking…" : "Check Status"}
          </button>

          {error && <p className="text-sm text-red-400">{error}</p>}
        </form>

        {/* Result */}
        <div className="px-5 pb-5" aria-live="polite">
          {loading && !status && <ShimmerCard />}
          {status?.status && (
            <>
              <StatusCard data={status} />
              <div className="mt-2 flex items-center justify-between text-xs text-textSecondary">
                <div className="flex items-center gap-2">
                  {isActive(status.status) ? <PulseDot /> : <span className="h-2 w-2 rounded-full bg-border inline-block" />}
                  <span>
                    {isActive(status.status) ? "Auto-refreshing" : "Final"} •{" "}
                    {lastUpdated ? `Updated ${formatTime(lastUpdated)}` : "—"}
                  </span>
                </div>
                {status.status === "FAIL" && (
                  <Link
  href={`/support/new?tx=${encodeURIComponent(status.transactionId)}`}
  className="underline hover:no-underline"
>
  Open Support Ticket
</Link>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusCard({ data }) {
  const transactionId = data?.transactionId ?? "—";
  const s = String(data?.status ?? "UNKNOWN").toUpperCase();
  const step = Number.isFinite(data?.step) ? data.step : 0;
  const message = data?.message ?? "Status unavailable.";

  return (
    <div className="mt-2 rounded-xl border border-border p-4 bg-background">
      {/* Header that wraps nicely */}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs text-textSecondary">Transaction</div>
          <div className="font-mono text-xs break-all sm:truncate" title={transactionId}>
            {transactionId}
          </div>
        </div>
        <Badge status={s} />
      </div>

      <div className="mt-3">
        <Progress step={step} />
        <p className="mt-2 text-sm text-textSecondary break-words">{message}</p>
      </div>
    </div>
  );
}

/** Color-coded badge */
function Badge({ status }) {
  const label = String(status ?? "UNKNOWN").replace(/_/g, " ");
  const color =
    status === "DONE"
      ? "bg-green-500/15 text-green-400 border-green-500/30"
      : status === "FAIL"
      ? "bg-red-500/15 text-red-400 border-red-500/30"
      : ["PENDING", "AUTHORIZED", "IN_PROGRESS"].includes(status)
      ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
      : "bg-zinc-500/10 text-zinc-300 border-zinc-500/20";

  return (
    <span
      className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs border ${color} shrink-0`}
    >
      {label}
    </span>
  );
}

function Progress({ step }) {
  const n = Number.isFinite(step) ? step : 0;
  const pct = Math.max(0, Math.min(100, (n / 4) * 100));
  return (
    <div className="w-full h-2 bg-surface rounded-full overflow-hidden">
      <div className="h-2 bg-accent transition-[width] duration-500" style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Skeleton while first load or between polls */
function ShimmerCard() {
  return (
    <div className="mt-2 rounded-xl border border-border p-4 bg-background animate-pulse">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="h-3 w-20 bg-surface rounded mb-1" />
          <div className="h-3 w-40 bg-surface rounded" />
        </div>
        <div className="h-6 w-20 bg-surface rounded-full" />
      </div>
      <div className="mt-3 h-2 w-full bg-surface rounded-full" />
      <div className="mt-2 h-3 w-3/4 bg-surface rounded" />
    </div>
  );
}

/** Tiny pulsing dot to show active polling */
function PulseDot() {
  return (
    <span className="relative inline-flex h-2 w-2">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
      <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
    </span>
  );
}

function formatTime(d) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(d);
  } catch {
    return d.toLocaleTimeString();
  }
}
